import React, { useState, useEffect, useCallback } from 'react';
import {
  Landmark, Plus, X, Loader2, Upload, CheckCircle, AlertCircle,
  ArrowDownCircle, ArrowUpCircle, Search, Link2, Trash2, ChevronLeft, ChevronRight, Eye, FileText, Building2
} from 'lucide-react';
import { conciliacionBancariaService, proveedoresService } from '../../../services/api';
import useModal from '../../../hooks/useModal';

const TABS = [
  { id: 'cuentas', label: 'Cuentas' },
  { id: 'movimientos', label: 'Movimientos' },
  { id: 'conciliar', label: 'Conciliar' }
];

const LINEAS_NEGOCIO = ['COT', 'MULTI', 'PUNTOV', 'DEV', 'IMP3D', 'GAMING', 'AUTO', 'GAMAN'];

const fmtFecha = (f) => (f ? new Date(f).toLocaleDateString('es-VE') : '—');
const fmtMonto = (n, moneda = 'VES') => {
  const val = Number(n) || 0;
  return moneda === 'USD' ? `$${val.toFixed(2)}` : `Bs. ${val.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ConciliacionBancariaView = () => {
  const { notify, confirm, ModalRenderer } = useModal();
  const [activeTab, setActiveTab] = useState('cuentas');
  const [cuentas, setCuentas] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNuevaCuenta, setShowNuevaCuenta] = useState(false);

  const [cuentaFiltro, setCuentaFiltro] = useState('');
  const [movimientos, setMovimientos] = useState([]);
  const [movimientosTotal, setMovimientosTotal] = useState(0);
  const [paginaMovimientos, setPaginaMovimientos] = useState(1);
  const [showImportar, setShowImportar] = useState(false);
  const [detalleMovimientoId, setDetalleMovimientoId] = useState(null);
  const LIMITE_MOVIMIENTOS = 25;

  const cargarCuentas = useCallback(async () => {
    try {
      const res = await conciliacionBancariaService.getCuentas();
      setCuentas(res.data || []);
    } catch (err) {
      setError(err.message || 'Error al cargar cuentas');
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [resBancos] = await Promise.all([proveedoresService.getBancos()]);
        setBancos(resBancos.data || []);
        await cargarCuentas();
      } catch (err) {
        setError(err.message || 'Error al cargar datos iniciales');
      } finally {
        setLoading(false);
      }
    })();
  }, [cargarCuentas]);

  const cargarMovimientos = useCallback(async (soloConciliado, pagina = 1) => {
    try {
      const filtros = { pagina, limite: LIMITE_MOVIMIENTOS };
      if (cuentaFiltro) filtros.cuenta_bancaria_id = cuentaFiltro;
      if (soloConciliado !== undefined) filtros.conciliado = soloConciliado;
      const res = await conciliacionBancariaService.getMovimientos(filtros);
      setMovimientos(res.data || []);
      setMovimientosTotal(res.meta?.total || 0);
      setPaginaMovimientos(pagina);
    } catch (err) {
      setError(err.message || 'Error al cargar movimientos');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuentaFiltro]);

  useEffect(() => {
    if (activeTab === 'movimientos') cargarMovimientos(undefined, 1);
    else if (activeTab === 'conciliar') cargarMovimientos(false, 1);
  }, [activeTab, cargarMovimientos]);

  return (
    <div className="p-2 md:p-6 space-y-6">
      <div>
        <h2 className="text-xl lg:text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Landmark className="text-blue-600" />
          Conciliación Bancaria
        </h2>
        <p className="text-sm text-gray-600">Cuentas de la empresa, movimientos importados del banco, y su conciliación contra cobros y pagos.</p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}

      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : (
        <>
          {activeTab === 'cuentas' && (
            <CuentasTab
              cuentas={cuentas}
              bancos={bancos}
              onNueva={() => setShowNuevaCuenta(true)}
              onEliminar={async (id) => {
                const ok = await confirm('Eliminar cuenta', '¿Eliminar esta cuenta bancaria?');
                if (!ok) return;
                try {
                  await conciliacionBancariaService.eliminarCuenta(id);
                  cargarCuentas();
                } catch (err) {
                  await notify('error', 'Error', err.message || 'Error al eliminar');
                }
              }}
            />
          )}

          {activeTab === 'movimientos' && (
            <MovimientosTab
              cuentas={cuentas}
              cuentaFiltro={cuentaFiltro}
              setCuentaFiltro={setCuentaFiltro}
              movimientos={movimientos}
              total={movimientosTotal}
              pagina={paginaMovimientos}
              limite={LIMITE_MOVIMIENTOS}
              onCambiarPagina={(p) => cargarMovimientos(undefined, p)}
              onImportar={() => setShowImportar(true)}
              onVerDetalle={(id) => setDetalleMovimientoId(id)}
              onEliminar={async (id) => {
                const ok = await confirm('Eliminar movimiento', '¿Eliminar este movimiento?');
                if (!ok) return;
                try {
                  await conciliacionBancariaService.eliminarMovimiento(id);
                  cargarMovimientos(undefined, paginaMovimientos);
                } catch (err) {
                  await notify('error', 'Error', err.message || 'Error al eliminar');
                }
              }}
            />
          )}

          {activeTab === 'conciliar' && (
            <ConciliarTab
              movimientos={movimientos}
              total={movimientosTotal}
              pagina={paginaMovimientos}
              limite={LIMITE_MOVIMIENTOS}
              onCambiarPagina={(p) => cargarMovimientos(false, p)}
              onConciliado={() => cargarMovimientos(false, paginaMovimientos)}
            />
          )}
        </>
      )}

      {showNuevaCuenta && (
        <NuevaCuentaModal
          bancos={bancos}
          onClose={() => setShowNuevaCuenta(false)}
          onSuccess={() => { setShowNuevaCuenta(false); cargarCuentas(); }}
        />
      )}

      {showImportar && (
        <ImportarMovimientosModal
          cuentas={cuentas}
          cuentaPreseleccionada={cuentaFiltro}
          onClose={() => setShowImportar(false)}
          onSuccess={() => { setShowImportar(false); cargarMovimientos(); }}
        />
      )}

      {detalleMovimientoId && (
        <DetalleConciliacionModal
          movimientoId={detalleMovimientoId}
          onClose={() => setDetalleMovimientoId(null)}
        />
      )}
      {ModalRenderer}
    </div>
  );
};

const CuentasTab = ({ cuentas, onNueva, onEliminar }) => (
  <div className="space-y-4">
    <div className="flex justify-end">
      <button onClick={onNueva} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
        <Plus size={16} /> Nueva Cuenta
      </button>
    </div>
    {cuentas.length === 0 ? (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">Sin cuentas bancarias registradas</div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cuentas.map(c => (
          <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-4 flex justify-between items-start">
            <div>
              <p className="font-semibold text-gray-800">{c.alias}</p>
              <p className="text-sm text-gray-500">{c.banco_nombre}</p>
              <p className="text-xs text-gray-400 mt-1">{c.numero_cuenta} · {c.tipo_cuenta} · {c.moneda}</p>
            </div>
            <button onClick={() => onEliminar(c.id)} className="text-gray-400 hover:text-red-600 p-1">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

const NuevaCuentaModal = ({ bancos, onClose, onSuccess }) => {
  const [form, setForm] = useState({ banco_codigo: '', numero_cuenta: '', alias: '', tipo_cuenta: 'corriente', moneda: 'VES' });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.banco_codigo || !form.numero_cuenta || !form.alias) {
      setError('Banco, número de cuenta y alias son requeridos');
      return;
    }
    setGuardando(true);
    try {
      await conciliacionBancariaService.crearCuenta(form);
      onSuccess();
    } catch (err) {
      setError(err.message || 'Error al crear la cuenta');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-bold">Nueva Cuenta Bancaria</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
            <select value={form.banco_codigo} onChange={(e) => setForm(f => ({ ...f, banco_codigo: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
              <option value="">Selecciona un banco...</option>
              {bancos.map(b => <option key={b.codigo} value={b.codigo}>{b.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Alias</label>
            <input value={form.alias} onChange={(e) => setForm(f => ({ ...f, alias: e.target.value }))} placeholder="Ej: Cuenta corriente principal" className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Número de cuenta</label>
            <input value={form.numero_cuenta} onChange={(e) => setForm(f => ({ ...f, numero_cuenta: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select value={form.tipo_cuenta} onChange={(e) => setForm(f => ({ ...f, tipo_cuenta: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="corriente">Corriente</option>
                <option value="ahorro">Ahorro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
              <select value={form.moneda} onChange={(e) => setForm(f => ({ ...f, moneda: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="VES">VES</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg">Cancelar</button>
            <button type="submit" disabled={guardando} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2">
              {guardando && <Loader2 size={16} className="animate-spin" />} Crear
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Paginacion = ({ pagina, limite, total, onCambiarPagina }) => {
  if (total === 0) return null;
  const totalPaginas = Math.max(1, Math.ceil(total / limite));
  const desde = (pagina - 1) * limite + 1;
  const hasta = Math.min(pagina * limite, total);
  return (
    <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
      <span>Mostrando {desde}–{hasta} de {total}</span>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onCambiarPagina(pagina - 1)}
          disabled={pagina <= 1}
          className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="text-xs">Página {pagina} de {totalPaginas}</span>
        <button
          onClick={() => onCambiarPagina(pagina + 1)}
          disabled={pagina >= totalPaginas}
          className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

const MovimientosTab = ({ cuentas, cuentaFiltro, setCuentaFiltro, movimientos, total, pagina, limite, onCambiarPagina, onImportar, onVerDetalle, onEliminar }) => (
  <div className="space-y-4">
    <div className="flex flex-wrap justify-between items-center gap-3">
      <select value={cuentaFiltro} onChange={(e) => setCuentaFiltro(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
        <option value="">Todas las cuentas</option>
        {cuentas.map(c => <option key={c.id} value={c.id}>{c.alias}</option>)}
      </select>
      <button onClick={onImportar} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
        <Upload size={16} /> Importar CSV
      </button>
    </div>

    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
              <th className="px-4 py-2">Fecha</th>
              <th className="px-4 py-2">Cuenta</th>
              <th className="px-4 py-2">Descripción</th>
              <th className="px-4 py-2">Referencia</th>
              <th className="px-4 py-2 text-right">Monto</th>
              <th className="px-4 py-2">Estado</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {movimientos.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Sin movimientos importados</td></tr>
            ) : movimientos.map(m => (
              <tr key={m.id} className="border-b border-gray-100">
                <td className="px-4 py-2 text-gray-500">{fmtFecha(m.fecha)}</td>
                <td className="px-4 py-2">{m.cuenta_alias}</td>
                <td className="px-4 py-2 max-w-xs truncate" title={m.deteccion?.nota || m.descripcion}>
                  {m.descripcion || '—'}
                  {m.deteccion && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700">
                      {m.deteccion.linea_negocio}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-gray-500">{m.referencia || '—'}</td>
                <td className={`px-4 py-2 text-right font-semibold flex items-center justify-end gap-1 ${m.tipo === 'credito' ? 'text-emerald-700' : 'text-red-700'}`}>
                  {m.tipo === 'credito' ? <ArrowDownCircle size={14} /> : <ArrowUpCircle size={14} />}
                  {fmtMonto(m.monto, m.cuenta_moneda)}
                </td>
                <td className="px-4 py-2">
                  {m.conciliado ? (
                    <button
                      onClick={() => onVerDetalle(m.id)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                    >
                      <CheckCircle size={12} /> Conciliado
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                      <AlertCircle size={12} /> Pendiente
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {m.conciliado ? (
                    <button onClick={() => onVerDetalle(m.id)} className="text-gray-400 hover:text-blue-600 p-1" title="Ver detalle">
                      <Eye size={14} />
                    </button>
                  ) : (
                    <button onClick={() => onEliminar(m.id)} className="text-gray-400 hover:text-red-600 p-1" title="Eliminar">
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Paginacion pagina={pagina} limite={limite} total={total} onCambiarPagina={onCambiarPagina} />
    </div>
  </div>
);

const ImportarMovimientosModal = ({ cuentas, cuentaPreseleccionada, onClose, onSuccess }) => {
  const [paso, setPaso] = useState(1);
  const [archivo, setArchivo] = useState(null);
  const [cuentaId, setCuentaId] = useState(cuentaPreseleccionada || '');
  const [preview, setPreview] = useState(null);
  const [mapeo, setMapeo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [resultado, setResultado] = useState(null);

  const handlePrevisualizar = async (e) => {
    e.preventDefault();
    if (!archivo || !cuentaId) {
      setError('Selecciona la cuenta y el archivo');
      return;
    }
    setCargando(true);
    setError('');
    try {
      const res = await conciliacionBancariaService.previsualizarImportacion(archivo);
      setPreview(res.data);
      setMapeo(res.data.mapeoSugerido);
      setPaso(2);
    } catch (err) {
      setError(err.message || 'Error al leer el archivo');
    } finally {
      setCargando(false);
    }
  };

  const handleImportar = async () => {
    setCargando(true);
    setError('');
    try {
      const res = await conciliacionBancariaService.importarMovimientos(archivo, cuentaId, mapeo);
      setResultado(res);
      setPaso(3);
    } catch (err) {
      setError(err.message || 'Error al importar');
    } finally {
      setCargando(false);
    }
  };

  const columnaSelect = (campo, label) => (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <select
        value={mapeo[campo] === null || mapeo[campo] === undefined ? '' : mapeo[campo]}
        onChange={(e) => setMapeo(m => ({ ...m, [campo]: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
        className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
      >
        <option value="">— No usar —</option>
        {preview.headers.map((h, idx) => <option key={idx} value={idx}>{h}</option>)}
      </select>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-bold">Importar Movimientos Bancarios</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <div className="p-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

          {paso === 1 && (
            <form onSubmit={handlePrevisualizar} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cuenta bancaria</label>
                <select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
                  <option value="">Selecciona la cuenta...</option>
                  {cuentas.map(c => <option key={c.id} value={c.id}>{c.alias}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Archivo CSV del banco</label>
                <input type="file" accept=".csv,text/csv" onChange={(e) => setArchivo(e.target.files[0])} className="w-full text-sm" required />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg">Cancelar</button>
                <button type="submit" disabled={cargando} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2">
                  {cargando && <Loader2 size={16} className="animate-spin" />} Leer archivo
                </button>
              </div>
            </form>
          )}

          {paso === 2 && preview && mapeo && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Se detectaron {preview.totalFilas} filas. Confirma qué columna corresponde a cada dato (se adivinaron según el encabezado):
              </p>
              <div className="grid grid-cols-2 gap-3">
                {columnaSelect('fecha', 'Fecha')}
                {columnaSelect('descripcion', 'Descripción')}
                {columnaSelect('referencia', 'Referencia')}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={mapeo.usaColumnasSeparadas}
                  onChange={(e) => setMapeo(m => ({ ...m, usaColumnasSeparadas: e.target.checked }))}
                />
                El banco separa crédito y débito en columnas distintas
              </label>
              {mapeo.usaColumnasSeparadas ? (
                <div className="grid grid-cols-2 gap-3">
                  {columnaSelect('credito', 'Columna de Crédito (entra)')}
                  {columnaSelect('debito', 'Columna de Débito (sale)')}
                </div>
              ) : (
                columnaSelect('monto', 'Columna de Monto (negativo = sale)')
              )}

              <div className="border border-gray-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50">
                      {preview.headers.map((h, i) => <th key={i} className="px-2 py-1.5 text-left font-medium text-gray-600">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.muestra.map((fila, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        {fila.map((celda, j) => <td key={j} className="px-2 py-1.5 text-gray-500">{celda}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setPaso(1)} className="px-4 py-2 border border-gray-300 rounded-lg">Atrás</button>
                <button onClick={handleImportar} disabled={cargando} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2">
                  {cargando && <Loader2 size={16} className="animate-spin" />} Importar
                </button>
              </div>
            </div>
          )}

          {paso === 3 && resultado && (
            <div className="space-y-4 text-center py-4">
              <CheckCircle size={48} className="text-emerald-500 mx-auto" />
              <p className="text-gray-800 font-medium">{resultado.message}</p>
              {resultado.data?.filasConError?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-left text-xs text-amber-800 max-h-32 overflow-y-auto">
                  {resultado.data.filasConError.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
              <button onClick={onSuccess} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Listo</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ConciliarTab = ({ movimientos, total, pagina, limite, onCambiarPagina, onConciliado }) => {
  const { notify, ModalRenderer } = useModal();
  const [expandidoId, setExpandidoId] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [cargandoCandidatos, setCargandoCandidatos] = useState(false);
  const [conciliandoId, setConciliandoId] = useState(null);
  const [conciliandoAuto, setConciliandoAuto] = useState(false);
  const [formIngresoId, setFormIngresoId] = useState(null);
  const [formIngreso, setFormIngreso] = useState({ linea_negocio: '', categoria: '', descripcion: '' });
  const [guardandoIngreso, setGuardandoIngreso] = useState(false);

  const handleConciliarAutomatico = async () => {
    setConciliandoAuto(true);
    try {
      const res = await conciliacionBancariaService.conciliarAutomatico();
      await notify('success', 'Listo', res.message);
      onConciliado();
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al conciliar automáticamente');
    } finally {
      setConciliandoAuto(false);
    }
  };

  const handleBuscar = async (movimientoId) => {
    if (expandidoId === movimientoId) { setExpandidoId(null); return; }
    setExpandidoId(movimientoId);
    setCargandoCandidatos(true);
    try {
      const res = await conciliacionBancariaService.getCandidatos(movimientoId);
      setCandidatos(res.data || []);
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al buscar candidatos');
    } finally {
      setCargandoCandidatos(false);
    }
  };

  const handleConfirmar = async (movimientoId, candidato) => {
    setConciliandoId(movimientoId);
    try {
      await conciliacionBancariaService.conciliar(movimientoId, candidato.tipo, candidato.id);
      setExpandidoId(null);
      onConciliado();
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al conciliar');
    } finally {
      setConciliandoId(null);
    }
  };

  const abrirFormIngreso = (movimiento) => {
    setFormIngresoId(movimiento.id);
    setFormIngreso({ linea_negocio: '', categoria: '', descripcion: movimiento.descripcion || '' });
  };

  const handleGuardarIngreso = async (movimientoId) => {
    if (!formIngreso.linea_negocio) {
      await notify('warning', 'Atención', 'Selecciona la línea de negocio');
      return;
    }
    setGuardandoIngreso(true);
    try {
      await conciliacionBancariaService.generarIngreso(movimientoId, formIngreso);
      setFormIngresoId(null);
      setExpandidoId(null);
      onConciliado();
    } catch (err) {
      await notify('error', 'Error', err.message || 'Error al generar el ingreso');
    } finally {
      setGuardandoIngreso(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          onClick={handleConciliarAutomatico}
          disabled={conciliandoAuto}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 text-sm font-medium"
        >
          {conciliandoAuto ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
          Conciliar automáticamente (monto + fecha + referencia exactos)
        </button>
      </div>

      {movimientos.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">No hay movimientos pendientes de conciliar</div>
      ) : movimientos.map(m => (
        <div key={m.id} className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <div className="flex items-center gap-3">
              {m.tipo === 'credito' ? <ArrowDownCircle size={18} className="text-emerald-600" /> : <ArrowUpCircle size={18} className="text-red-600" />}
              <div>
                <p className="font-medium text-gray-800">
                  {m.descripcion || '(sin descripción)'}
                  {m.deteccion && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-purple-100 text-purple-700" title={m.deteccion.nota}>
                      {m.deteccion.linea_negocio}
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500">{fmtFecha(m.fecha)} · {m.cuenta_alias} · Ref: {m.referencia || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`font-semibold ${m.tipo === 'credito' ? 'text-emerald-700' : 'text-red-700'}`}>{fmtMonto(m.monto, m.cuenta_moneda)}</span>
              <button
                onClick={() => handleBuscar(m.id)}
                className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs font-medium"
              >
                <Search size={12} /> {expandidoId === m.id ? 'Ocultar' : 'Buscar coincidencias'}
              </button>
            </div>
          </div>

          {expandidoId === m.id && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              {cargandoCandidatos ? (
                <p className="text-sm text-gray-400">Buscando...</p>
              ) : candidatos.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Sin coincidencias por ahora. Registra el cobro/pago correspondiente e intenta de nuevo.</p>
              ) : (
                <div className="space-y-2">
                  {candidatos.map((c, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-gray-50 rounded-lg p-2.5">
                      <div>
                        <p className="text-sm text-gray-800">
                          {c.descripcion}
                          {c.coincidenciaExacta && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-700">
                              Exacto
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">{fmtFecha(c.fecha)} · {fmtMonto(c.monto, c.moneda)} · puntaje {c.score}</p>
                      </div>
                      <button
                        onClick={() => handleConfirmar(m.id, c)}
                        disabled={conciliandoId === m.id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-medium disabled:bg-gray-300"
                      >
                        {conciliandoId === m.id ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
                        Confirmar
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {!cargandoCandidatos && m.tipo === 'credito' && (
                formIngresoId === m.id ? (
                  <div className="mt-3 bg-gray-50 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-600">Registrar como ingreso nuevo</p>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={formIngreso.linea_negocio}
                        onChange={e => setFormIngreso({ ...formIngreso, linea_negocio: e.target.value })}
                        className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                      >
                        <option value="">Línea de negocio...</option>
                        {LINEAS_NEGOCIO.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                      <input
                        type="text"
                        placeholder="Categoría"
                        value={formIngreso.categoria}
                        onChange={e => setFormIngreso({ ...formIngreso, categoria: e.target.value })}
                        className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Descripción"
                      value={formIngreso.descripcion}
                      onChange={e => setFormIngreso({ ...formIngreso, descripcion: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setFormIngresoId(null)}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => handleGuardarIngreso(m.id)}
                        disabled={guardandoIngreso}
                        className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-medium disabled:bg-gray-300"
                      >
                        {guardandoIngreso ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                        Guardar
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => abrirFormIngreso(m)}
                    className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-white border border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 text-xs font-medium"
                  >
                    <Plus size={12} /> Registrar como ingreso nuevo
                  </button>
                )
              )}
            </div>
          )}
        </div>
      ))}
      {total > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <Paginacion pagina={pagina} limite={limite} total={total} onCambiarPagina={onCambiarPagina} />
        </div>
      )}
      {ModalRenderer}
    </div>
  );
};

const TIPO_LABEL = {
  factura_compra: 'Factura de compra',
  validacion_pago: 'Cobro (ventas)',
  egreso: 'Egreso general',
  ingreso: 'Ingreso general'
};

const CampoDetalle = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-medium text-gray-800">{value ?? '—'}</p>
  </div>
);

const DetalleConciliacionModal = ({ movimientoId, onClose }) => {
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await conciliacionBancariaService.getDetalle(movimientoId);
        setDetalle(res.data);
      } catch (err) {
        setError(err.message || 'Error al obtener el detalle');
      } finally {
        setLoading(false);
      }
    })();
  }, [movimientoId]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
          <h3 className="text-lg font-bold">Detalle de la conciliación</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <div className="p-4 space-y-4">
          {loading && (
            <div className="text-center py-8">
              <Loader2 size={24} className="animate-spin mx-auto text-blue-600" />
            </div>
          )}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

          {detalle && (
            <>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">
                {TIPO_LABEL[detalle.tipo] || detalle.tipo}
              </span>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <Building2 size={14} /> Proveedor / contraparte
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <CampoDetalle label="Nombre" value={detalle.proveedor?.nombre} />
                  <CampoDetalle label="RIF" value={detalle.proveedor?.rif} />
                </div>
                {detalle.tipo === 'egreso' && !detalle.proveedor?.nombre && (
                  <p className="text-xs text-gray-400 italic mt-2">
                    Este egreso no referencia un proveedor identificable en el texto de la descripción.
                  </p>
                )}
                {detalle.tipo === 'ingreso' && (
                  <p className="text-xs text-gray-400 italic mt-2">
                    Este ingreso no proviene de un cliente facturado (p.ej. liquidación de puntos de venta), no aplica contraparte.
                  </p>
                )}
                {detalle.proveedor?.detectado_del_texto && (
                  <p className="text-xs text-amber-600 italic mt-2">
                    Nombre detectado a partir del texto de la descripción, sin RIF verificado.
                  </p>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <FileText size={14} /> Detalle del pago
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <CampoDetalle label="Fecha" value={fmtFecha(detalle.fecha)} />
                  {detalle.tipo === 'factura_compra' && (
                    <>
                      <CampoDetalle label="N° Factura" value={detalle.pago.numero_proveedor} />
                      <CampoDetalle label="N° Control" value={detalle.pago.numero_control_proveedor} />
                      <CampoDetalle label="Estado" value={detalle.pago.estado} />
                      <CampoDetalle label="Base imponible" value={fmtMonto(detalle.pago.base_imponible, detalle.pago.moneda)} />
                      <CampoDetalle label="Exento" value={fmtMonto(detalle.pago.base_exenta, detalle.pago.moneda)} />
                      <CampoDetalle label="Impuesto" value={fmtMonto(detalle.pago.impuesto, detalle.pago.moneda)} />
                      <CampoDetalle label="Total" value={fmtMonto(detalle.pago.total, detalle.pago.moneda)} />
                      <CampoDetalle label="Tasa" value={detalle.pago.tasa} />
                      <CampoDetalle label="Método de pago" value={detalle.pago.metodo_pago} />
                      <CampoDetalle label="Referencia de pago" value={detalle.pago.referencia_pago} />
                    </>
                  )}
                  {detalle.tipo === 'validacion_pago' && (
                    <>
                      <CampoDetalle label="N° Factura" value={detalle.pago.numero_factura} />
                      <CampoDetalle label="Cotización" value={detalle.pago.cotizacion_numero} />
                      <CampoDetalle label="Monto" value={fmtMonto(detalle.pago.monto, detalle.pago.moneda)} />
                      <CampoDetalle label="Referencia" value={detalle.pago.referencia} />
                      <CampoDetalle label="Quien paga" value={detalle.pago.quien_paga} />
                    </>
                  )}
                  {detalle.tipo === 'egreso' && (
                    <>
                      <CampoDetalle label="Código" value={detalle.pago.codigo} />
                      <CampoDetalle label="Descripción" value={detalle.pago.descripcion} />
                      <CampoDetalle label="Línea de negocio" value={detalle.pago.linea_negocio} />
                      <CampoDetalle label="Subservicio" value={detalle.pago.subservicio} />
                      <CampoDetalle label="Categoría" value={detalle.pago.categoria} />
                      <CampoDetalle label="Monto USD" value={detalle.pago.monto_usd !== null ? fmtMonto(detalle.pago.monto_usd, 'USD') : '—'} />
                      <CampoDetalle label="Monto Bs" value={detalle.pago.monto_bs !== null ? fmtMonto(detalle.pago.monto_bs, 'VES') : '—'} />
                      <CampoDetalle label="Cuenta de origen" value={detalle.pago.cuenta_origen} />
                    </>
                  )}
                  {detalle.tipo === 'ingreso' && (
                    <>
                      <CampoDetalle label="Descripción" value={detalle.pago.descripcion} />
                      <CampoDetalle label="Línea de negocio" value={detalle.pago.linea_negocio} />
                      <CampoDetalle label="Categoría" value={detalle.pago.categoria} />
                      <CampoDetalle label="Monto" value={fmtMonto(detalle.pago.monto, detalle.pago.moneda)} />
                      <CampoDetalle label="Referencia bancaria" value={detalle.pago.referencia_bancaria} />
                      <CampoDetalle label="Cuenta destino" value={detalle.pago.cuenta_destino} />
                      <CampoDetalle label="Origen" value={detalle.pago.origen === 'deteccion_automatica' ? 'Detección automática' : 'Manual'} />
                    </>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-semibold text-gray-700 mb-2">Retenciones</div>
                {detalle.retenciones.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">
                    {detalle.tipo === 'egreso'
                      ? 'Este egreso no tiene retención registrada en el sistema (el ledger de origen no la trae).'
                      : detalle.tipo === 'ingreso'
                      ? 'Este ingreso no tiene retención registrada en el sistema (el ledger de origen no la trae).'
                      : 'Sin retenciones asociadas.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {detalle.retenciones.map(r => (
                      <div key={r.id} className="bg-white rounded-lg p-2.5 border border-gray-200">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium text-gray-800">{r.impuesto?.toUpperCase()} · {r.comprobante || 'sin comprobante'}</span>
                          <span className="text-gray-500">{fmtFecha(r.fecha)}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-1 text-xs text-gray-500">
                          <span>Base: {fmtMonto(r.base, r.moneda)}</span>
                          <span>% aplicado: {(Number(r.porcentaje_aplicado) * 100).toFixed(1)}%</span>
                          <span>Retenido: {fmtMonto(r.monto, r.moneda)}</span>
                          <span>Estado: {r.estado}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConciliacionBancariaView;
